import { memo } from "react";
import { CollapsibleSection, SpecialComponentProps } from "../../../index";
import { Button, Checkbox, Combobox, Flex, Radio } from "@repo/ping-design";

import "./ReaskStormTrack.scss";
import { useMemo } from "react";
import {
  ReaskFilterOptions,
  SPEED_UNITS as SPEED_UNITS_TYPE,
  TCWindBasinFilter,
  TCWindOptions,
} from "./ReaskStormTrackTypes";

type ReaskReduxHooks = {
  tcWindOptions: TCWindOptions;
  setTCWindOptions: (TCWindOptions: TCWindOptions) => void;
  tcWindMode: boolean;
  setTCWindMode: (boolean: boolean) => void;
};

interface ReaskStormTrackProps extends SpecialComponentProps {
  TCStormHistoryLoading: boolean;
  selectedStormId: string;
  loadingStorm: boolean;
  stormError: string;
  SPEED_UNITS: typeof SPEED_UNITS_TYPE;
  fetchAndUpdateStormById: (stormId: string) => void;
  REASK_DISABLED?: boolean;
  reaskFilterOptions: ReaskFilterOptions;
  reaskReduxHooks: ReaskReduxHooks;
}

const BOUNDINGBOXENABLED = false;
export const ReaskStormTrack = memo(
  ({
    onLayerSelect,
    onClose,
    TCStormHistoryLoading,
    selectedStormId,
    loadingStorm,
    stormError,
    SPEED_UNITS,
    fetchAndUpdateStormById,
    REASK_DISABLED = false,
    reaskFilterOptions: filterOptions,
    reaskReduxHooks: reduxHooks,
  }: ReaskStormTrackProps) => {
    console.log("filterOptions", filterOptions);
    const loadingString = useMemo(() => {
      const loading = TCStormHistoryLoading || loadingStorm ? "{Loading}" : "";
      const error = stormError != null ? `{${stormError}}` : "";

      if (loading) {
        return loading;
      } else if (error) {
        return error;
      } else {
        return "";
      }
    }, [TCStormHistoryLoading, loadingStorm, stormError]);

    return (
      <CollapsibleSection
        title={
          !REASK_DISABLED
            ? `Reask ${loadingString}`
            : `Reask Currently Disabled`
        }
      >
        <Flex direction="row" align="center" gap="2">
          <Combobox
            selected={reduxHooks.tcWindOptions.BasinFilter}
            options={filterOptions.basinOptions}
            variant="translucent"
            label="Basin"
            orientation="up"
            className="PingMapWorkspaceBuildingFiltersV2__Filters__Dropdown"
            placeholder={"All"}
            selectionType="multiple"
            disabled={loadingStorm || TCStormHistoryLoading || REASK_DISABLED}
            onChange={(changed) => {
              const res: TCWindOptions = {
                ...reduxHooks.tcWindOptions,
                BasinFilter: changed.value as TCWindBasinFilter,
              };
              reduxHooks.setTCWindOptions(res as TCWindOptions);
            }}
          />
          <Combobox
            selected={reduxHooks.tcWindOptions.YearFilter}
            options={filterOptions.yearOptions}
            variant="translucent"
            label="Year"
            orientation="up"
            className="PingMapWorkspaceBuildingFiltersV2__Filters__Dropdown"
            placeholder={"All"}
            selectionType="multiple"
            disabled={loadingStorm || TCStormHistoryLoading || REASK_DISABLED}
            onChange={(changed) => {
              const res: TCWindOptions = {
                ...reduxHooks.tcWindOptions,
                YearFilter: changed.value as unknown as string[],
              };

              reduxHooks.setTCWindOptions(res as TCWindOptions);
            }}
          />
        </Flex>
        <Flex direction="column" gap="1">
          <Combobox
            selected={selectedStormId}
            options={filterOptions.locationOptions}
            variant="translucent"
            label="Storm Name"
            orientation="up"
            className="PingMapWorkspaceBuildingFiltersV2__Filters__Dropdown"
            placeholder={"Storm (Year)"}
            selectionType="single"
            disabled={loadingStorm || TCStormHistoryLoading || REASK_DISABLED}
            onChange={(changed) => {
              const selectedStormId = changed.value as string;
              fetchAndUpdateStormById(selectedStormId);
            }}
          />
          <div className="filters-container-grid">
            <Flex direction="column" gap="2">
              <div className="filters-title">Display</div>
              <Flex
                direction="row"
                gap="1-5"
                className="filters-container-option"
              >
                <Checkbox
                  label={"Track"}
                  checked={reduxHooks.tcWindOptions.TrackEnabled}
                  onChange={() => {
                    const res: TCWindOptions = {
                      ...reduxHooks.tcWindOptions,
                      TrackEnabled: !reduxHooks.tcWindOptions.TrackEnabled,
                    };

                    reduxHooks.setTCWindOptions(res as TCWindOptions);
                  }}
                  disabled={REASK_DISABLED}
                />

                <Checkbox
                  label={"Footprint"}
                  checked={reduxHooks.tcWindOptions.FootprintEnabled}
                  onChange={() => {
                    const res: TCWindOptions = {
                      ...reduxHooks.tcWindOptions,
                      FootprintEnabled:
                        !reduxHooks.tcWindOptions.FootprintEnabled,
                    };
                    reduxHooks.setTCWindOptions(res as TCWindOptions);
                  }}
                  disabled={REASK_DISABLED}
                />
              </Flex>
              <div className="storm-disclaimer">
                Only Track can overlay when multiple storms are selected
              </div>
            </Flex>
            <Flex direction="column" gap="2">
              <div className="filters-title">Units</div>
              <Flex
                direction="row"
                gap="1-5"
                className="filters-container-option"
              >
                <Radio
                  name="Speed Units"
                  checked={reduxHooks.tcWindOptions.UnitName == SPEED_UNITS.MPH}
                  label={SPEED_UNITS.MPH}
                  onChange={() => {
                    const toggled_unit_name =
                      reduxHooks.tcWindOptions.UnitName === SPEED_UNITS.MPH
                        ? SPEED_UNITS.KPH
                        : SPEED_UNITS.MPH;

                    const res: TCWindOptions = {
                      ...reduxHooks.tcWindOptions,
                      UnitName: toggled_unit_name,
                    };
                    reduxHooks.setTCWindOptions(res as TCWindOptions);
                  }}
                  disabled={REASK_DISABLED}
                />
                <Radio
                  checked={reduxHooks.tcWindOptions.UnitName == SPEED_UNITS.KPH}
                  label={SPEED_UNITS.KPH}
                  onChange={() => {
                    const toggled_unit_name =
                      reduxHooks.tcWindOptions.UnitName === SPEED_UNITS.MPH
                        ? SPEED_UNITS.KPH
                        : SPEED_UNITS.MPH;

                    const res: TCWindOptions = {
                      ...reduxHooks.tcWindOptions,
                      UnitName: toggled_unit_name,
                    };
                    reduxHooks.setTCWindOptions(res as TCWindOptions);
                  }}
                  disabled={REASK_DISABLED}
                />
              </Flex>
            </Flex>
          </div>

          {BOUNDINGBOXENABLED && (
            <Flex direction="row" align="center" gap="2">
              <Button
                label={`Bounding Box (${reduxHooks.tcWindMode})`}
                onClick={() => {
                  reduxHooks.setTCWindMode(!reduxHooks.tcWindMode);
                }}
                variant="outline"
                disabled={REASK_DISABLED}
              />
            </Flex>
          )}
        </Flex>
      </CollapsibleSection>
    );
  },
);

ReaskStormTrack.displayName = "ReaskStormTrack";
